import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";

const EMPTY = { code: "", type: "percent", value: "", min_subtotal: "", max_uses: "", active: true, expires_at: "" };
const input = "w-full h-10 bg-white/5 border border-white/15 focus:border-[#FF3B30] outline-none px-3 text-sm";

const F = ({ label, children, span }) => (
  <label className={`block ${span ? "col-span-2" : ""}`}><span className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">{label}</span><div className="mt-1">{children}</div></label>
);

export const DiscountFormDialog = ({ open, onOpenChange, discount, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setF(discount ? { ...discount, min_subtotal: discount.min_subtotal || "", max_uses: discount.max_uses ?? "", expires_at: discount.expires_at ? discount.expires_at.slice(0, 10) : "" } : EMPTY);
  }, [discount, open]);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    const body = { code: f.code, type: f.type, value: parseFloat(f.value), min_subtotal: parseFloat(f.min_subtotal) || 0,
      max_uses: f.max_uses === "" ? null : parseInt(f.max_uses, 10), active: f.active, expires_at: f.expires_at ? `${f.expires_at}T23:59:59Z` : null };
    try {
      discount ? await api.put(`/admin/discounts/${discount.id}`, body) : await api.post("/admin/discounts", body);
      toast.success(discount ? "Discount updated" : "Discount created"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="discount-form-dialog" className="bg-[#121215] border-white/10 max-w-lg rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display uppercase font-black text-2xl">{discount ? "Edit discount" : "New discount code"}</DialogTitle>
          <DialogDescription className="text-neutral-500 text-xs">Customers enter this code in their cart before checkout.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-4">
          <F label="Code"><input required data-testid="discount-form-code" className={`${input} uppercase font-mono`} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="ABS10" /></F>
          <F label="Type">
            <select data-testid="discount-form-type" className={input} value={f.type} onChange={on("type")}>
              <option value="percent" className="bg-black">Percent off</option>
              <option value="fixed" className="bg-black">Fixed amount off (USD)</option>
            </select>
          </F>
          <F label={f.type === "percent" ? "Percent (%)" : "Amount (USD)"}><input required type="number" step="0.01" min="0.01" max={f.type === "percent" ? 100 : undefined} data-testid="discount-form-value" className={input} value={f.value} onChange={on("value")} /></F>
          <F label="Min. subtotal (USD)"><input type="number" step="0.01" min="0" data-testid="discount-form-min" className={input} value={f.min_subtotal} onChange={on("min_subtotal")} placeholder="0" /></F>
          <F label="Max uses"><input type="number" min="1" data-testid="discount-form-max-uses" className={input} value={f.max_uses} onChange={on("max_uses")} placeholder="Unlimited" /></F>
          <F label="Expires on"><input type="date" data-testid="discount-form-expires" className={input} value={f.expires_at} onChange={on("expires_at")} /></F>
          <F label="Active" span><div className="h-10 flex items-center"><Switch data-testid="discount-form-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /></div></F>
          <button data-testid="discount-form-submit" disabled={saving} className="col-span-2 h-11 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-black tracking-wider disabled:opacity-60">
            {saving ? "Saving..." : "Save discount"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
